/**
 * An <img> bound to a landing image slot.
 *
 * Renders exactly like a plain <img> for visitors. For a signed-in admin it
 * also gets a hover overlay with Replace / Reset controls — nothing about the
 * markup or layout changes, so the design is unaffected either way.
 */

import React, { useCallback, useId, useRef, useState } from 'react';
import { ImageUp, Link2, Loader2, RotateCcw } from 'lucide-react';
import EditableText from './EditableText';
import { useLandingImages, slotMeta } from './useLandingImages';
import type { LandingSlot } from './landingImages';

export interface EditableImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  slot: LandingSlot;
  /** Wrapper class — the overlay needs a positioned parent. */
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
  /**
   * Content key holding the URL this image links to. When given, the overlay
   * grows a second control for editing that destination — so an admin sets
   * both the picture and where clicking it goes, from the same place.
   */
  linkKey?: string;
  /** Destination used until the key is overridden. */
  linkFallback?: string;
}

export const EditableImage = React.forwardRef<HTMLImageElement, EditableImageProps>(
  ({ slot, wrapperClassName, wrapperStyle, linkKey, linkFallback = '', ...imgProps }, ref) => {
    const { src, canEdit, savingSlot, replace, reset, isOverridden } = useLandingImages();
    const inputRef = useRef<HTMLInputElement>(null);
    const inputId = useId();
    const [localError, setLocalError] = useState<string | null>(null);

    const meta = slotMeta(slot);
    const saving = savingSlot === slot;

    const onPick = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Clear immediately so picking the same file twice still fires onChange.
        e.target.value = '';
        if (!file) return;
        setLocalError(null);
        try {
          await replace(slot, file);
        } catch (err: any) {
          setLocalError(err?.message ?? 'Upload failed');
        }
      },
      [replace, slot],
    );

    const onReset = useCallback(async () => {
      setLocalError(null);
      try {
        await reset(slot);
      } catch (err: any) {
        setLocalError(err?.message ?? 'Reset failed');
      }
    }, [reset, slot]);

    const image = <img ref={ref} src={src(slot)} {...imgProps} />;

    // Visitors get the bare <img> — no wrapper, no behaviour, no layout shift.
    if (!canEdit) return image;

    return (
      <span className={`elf-edit ${wrapperClassName ?? ''}`.trim()} style={wrapperStyle}>
        {image}
        <span className="elf-edit__ui">
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
            className="elf-edit__input"
            onChange={onPick}
          />
          <button
            type="button"
            className="elf-edit__btn"
            disabled={saving}
            title={`Replace: ${meta.label} — ${meta.hint}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            {saving ? (
              <Loader2 size={14} className="elf-edit__spin" aria-hidden="true" />
            ) : (
              <ImageUp size={14} aria-hidden="true" />
            )}
            <span>{saving ? 'Saving…' : meta.label}</span>
          </button>
          {linkKey && (
            <EditableText
              contentKey={linkKey}
              fallback={linkFallback}
              label="Redirect link"
              className="elf-edit__btn elf-edit__btn--link"
              underline={false}
            >
              {() => (
                <>
                  <Link2 size={13} aria-hidden="true" />
                  <span>Link</span>
                </>
              )}
            </EditableText>
          )}
          {isOverridden(slot) && (
            <button
              type="button"
              className="elf-edit__btn elf-edit__btn--icon"
              disabled={saving}
              title="Restore the original image"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void onReset();
              }}
            >
              <RotateCcw size={13} aria-hidden="true" />
            </button>
          )}
        </span>
        {localError && <span className="elf-edit__err">{localError}</span>}
      </span>
    );
  },
);

EditableImage.displayName = 'EditableImage';

export default EditableImage;
